import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { KEYS_TAGS } from '@components/imageHandling/common';
import { checkEvent } from '@reducers/utils';
import type { OperationSearchItem } from '@interfaces/search';
import type { UploadImageDocumentSignalRResponse } from '@v2/components/imageHandling/types/types';

interface UseListenForUploadedOperationImagesParams {
  isActiveInstance: boolean;
  setJobs: Dispatch<SetStateAction<OperationSearchItem[]>>;
}

const PROCESSED_DOCUMENT_EVENTS = ['OnProcessedImageDocument', 'OnProcessedOtherDocument'] as const;

const getOperationLinks = (data: UploadImageDocumentSignalRResponse) =>
  (data.image?.links ?? []).filter((link) => link?.scopeId === KEYS_TAGS.Operation && link?.scopeReferenceId != null);

export const useListenForUploadedOperationImages = ({ isActiveInstance, setJobs }: UseListenForUploadedOperationImagesParams) => {
  useEffect(() => {
    if (!isActiveInstance) return;

    const controller = new AbortController();

    const listen = async (eventName: string) => {
      while (!controller.signal.aborted) {
        try {
          const data = (await checkEvent(eventName, { signal: controller.signal })) as UploadImageDocumentSignalRResponse;
          if (controller.signal.aborted) return;
          if (!data || data.isError || !data.image) continue;

          const operationLinks = getOperationLinks(data);
          if (!operationLinks.length) continue;

          setJobs((prev) =>
            prev.map((job) => {
              const operationLink = operationLinks.find((link) => link.scopeReferenceId === job.productionOperationId);
              if (!operationLink) return job;

              const pageNumber = data.image?.pageNumber ?? job.imageData?.pageNumber ?? 1;
              const pageDetails = data.image?.pageDetails?.find((page) => page.pageNumber === pageNumber);

              return {
                ...job,
                imageDocumentId: data.image?.documentId ?? data.documentId ?? job.imageDocumentId,
                imageHash: data.image?.contentHash ?? job.imageHash,
                imageData: {
                  ...job.imageData,
                  isCropped: data.image?.isCropped ?? job.imageData?.isCropped,
                  extensionDerivative: data.image?.extensionDerivative ?? job.imageData?.extensionDerivative,
                  pageNumber,
                  rotationAngle: pageDetails?.rotationAngle ?? job.imageData?.rotationAngle ?? 0,
                },
              };
            })
          );
        } catch {
          // Re-arm on timeout/error so the listener keeps running for the lifetime of the screen.
          // On abort the next while-check exits the loop.
        }
      }
    };

    PROCESSED_DOCUMENT_EVENTS.forEach((eventName) => {
      void listen(eventName);
    });

    return () => {
      controller.abort();
    };
  }, [isActiveInstance, setJobs]);
};
